import { presetPalettes } from '@ant-design/colors';

export function lighten(hexColor: string, percentage: number) {
  // Remove the '#' if present
  hexColor = hexColor.startsWith('#') ? hexColor.slice(1) : hexColor;

  // Handle invalid hex color length
  if (hexColor.length !== 6) {
    console.warn("Invalid hex color format. Please provide a 6-digit hex code.");
    return hexColor; // Return original color or throw an error
  }

  // Clamp percentage to 0-100 range
  percentage = Math.max(0, Math.min(100, percentage));

  // Convert hex to decimal
  let r = parseInt(hexColor.substring(0, 2), 16);
  let g = parseInt(hexColor.substring(2, 4), 16);
  let b = parseInt(hexColor.substring(4, 6), 16);

  // Lighten each component by percentage (move towards 255)
  r = Math.min(255, r + (255 - r) * (percentage / 100));
  g = Math.min(255, g + (255 - g) * (percentage / 100));
  b = Math.min(255, b + (255 - b) * (percentage / 100));

  // Convert back to hex and format
  const toHex = (c: number) => Math.round(c).toString(16).padStart(2, '0');
  return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
}

export const { blue, red, green, gold, yellow } = presetPalettes;
export const white = '#ffffff';
export const black = '#292929';
export const primary = '#eb2f96';
export const secondary = '#570A57';
export const ink = '#17111b';

// Marketing palette: ink is the shared dark anchor, magenta is reserved for
// actions, and teal only ever signals completion.
export const footerBg = ink;
export const footerText = '#f4f2f5';
export const footerLink = '#ddd9e0';
export const footerMuted = '#b8b1bc';
export const magenta = '#d31f83';
export const teal = '#0b6f63';
export const tealTint = '#e6f4f1';
export const inkBody = '#453a4c';
export const inkMuted = '#4a4350';
export const inkFaint = '#7a7280';
export const inkFainter = '#9a94a1';
// Secondary text inside the app. `inkFaint` only reaches 4.20:1 on canvasAlt,
// which the app uses as its page canvas; this clears 4.5:1 on both surfaces.
export const inkSubtle = '#645d6b';
export const canvas = '#ffffff';
export const canvasAlt = '#faf8f9';
export const hairline = 'rgba(23, 19, 27, 0.08)';

// --- Poplearn brand ---
// One action color (pop), a set of flat pastel fills, and a hard ink outline.
// Nothing carries a shadow — depth comes from the 2px border.
//
// The action pink clears AA with white text (4.90:1), so buttons and links can
// use the documented brand color without compromising legibility.
export const pop = '#e5084b';
export const popDeep = '#c9134a';
export const outline = ink;

export const butter = '#fbe8bf';
export const butterSoft = '#fff9ea';
export const butterNote = '#fdf2d6';
export const butterInk = '#7a5a12';
export const lilac = '#f3e8fd';
export const lilacSoft = '#f7e6fd';
export const mint = '#e4f8ee';
export const blush = '#fdeaf0';
export const sky = '#def2f7';
export const dash = '#cfc7d4';

// Poppins is the product typeface — headings, labels, buttons, table cells and
// body copy.
// 'Poppins Fallback' is a metrics-matched alias for Arial declared in the page
// head. It stands in for the real face while the CDN request is in flight and
// occupies the same space, so the swap when Poppins lands reflows nothing.
// Keep it between Poppins and sans-serif in every stack.
export const displayFont = "Poppins, 'Poppins Fallback', sans-serif";
export const bodyFont = "Poppins, 'Poppins Fallback', sans-serif";

/**
 * The wordmark alone. Sour Gummy's heavy, soft-cornered strokes echo the
 * popcorn mark's shapes in a way Poppins' flat geometric terminals can't — and
 * keeping it off `displayFont` means the logo stays a fixed piece of artwork
 * even if the UI typeface changes again.
 *
 * Only weight 900 is loaded, which is the weight the wordmark uses.
 */
export const wordmarkFont = "'Sour Gummy', sans-serif";
export const monoFont = "'IBM Plex Mono', ui-monospace, Menlo, monospace";
export const gray = [
  '#ffffff', '#fafafa', '#f5f5f5', '#f0f0f0',
  '#d9d9d9', '#bfbfbf', '#8c8c8c', '#595959',
  '#434343', '#292929'
];

// Default export for backwards compatibility
const palette = {
  primary,
  secondary,
  // Add more colors as needed
};

export default palette;
